/**
 * The Alluzo colour primitives.
 *
 * **Do not use these in app code.** A primitive says what a colour *is*, not
 * what it *means*. App code uses the semantic token names on a component's
 * props, such as `backgroundColor="surface.background.gray.subtle"`.
 * `theme/colors.ts` maps these primitives onto those names.
 *
 * The step numbers follow the Tailwind scale, so `50` is the lightest tint and
 * `900` is the strongest.
 *
 * **The system went black and white on 9 Sep 2026.** The primary was orange,
 * hue 29, taken from the app icon. It is now `ink`, a neutral ramp, and the
 * grey ramps went back to chroma 0. The reference is Uber's Base: black and
 * white carries the structure, and colour is spent only where it means
 * something. In a ledger that is cash in and cash out, which is most rows, so
 * the neutral has to stay out of their way.
 *
 * Three ramps moved and the role hues did not.
 *
 * - `brand` became `ink`. Step 600 is the primary fill and step 650 the press.
 * - Both `gray` ramps went from hue 65 at chroma 4 to 17 back to chroma 0. The
 *   warm cut existed to relate the neutral to an orange brand; with the primary
 *   drawn from the neutral itself that premise is gone, and a cold ramp reads
 *   as the deliberately utilitarian surface this product wants.
 * - `darkPalette.brand` became `darkPalette.ink` and **inverts**: the dark
 *   primary is a near-white fill carrying near-black ink, because a near-black
 *   fill on a near-black screen is not a button.
 *
 * **Every lightness was preserved to within 0.20 of a point.** The cold values
 * were solved for the same OKLab lightness the warm ones had, so no contrast
 * relationship in the system moved: body ink on a card went 16.13 to 16.10,
 * muted text on the screen 4.57 to 4.61. Only the chroma left.
 *
 * The light `gray` ramp is the Material grey ramp this file started from, which
 * commit `473fa8d` kept and the 30 Aug re-cut warmed. Steps 500, 600 and 700
 * are the exception: they sit darker than Material, because the warm re-cut
 * dropped them so disabled text clears 3.0 and muted text clears 4.5 on the
 * screen rather than only on a card. That fix is accessibility work and it
 * survives the return to cold.
 *
 * `green`, `red`, `navy` and `purple` hold their hue, so cash in and cash out
 * keep the colours users already know. `amber` carries `notice`; it was re-hued
 * away from the brand in Aug 2026 and, with no brand hue left to collide with,
 * it stays where it is.
 *
 * **There is no accent hue.** Nothing is interactive by being a different
 * colour from the text beside it, and the primary is ink in both schemes. An
 * action is carried by shape: an underline, a weight, a chevron, an ink fill,
 * an ink border, or an inversion. A selection is carried by an ink border, an
 * inversion, a real control, or a neutral tint — in that order of preference.
 * `docs/design-system/foundations/colour.md` says which treatment belongs to which control, and
 * it is the contract.
 *
 * Colour is reserved for meaning: `green` and `red` for cash in and cash out,
 * `amber` for a warning, `navy` for `information`.
 */

/** One colour ramp. The key is the Tailwind step. */
export type Ramp = Readonly<Record<number, string>>;

/** Light mode primitives. */
export const palette = {
  /**
   * The primary. A neutral ink ramp, chroma 0.
   *
   * Step 600 is the primary fill and the strongest ink in the system. It sits
   * 6.1 lightness points below `gray[900]`, which is body text, and 13.1 below
   * `gray[800]`, which is the neutral button — so a primary button, a neutral
   * button and a paragraph are three distinguishable things.
   *
   * White on step 600 reads 18.58:1. The orange it replaced read 3.03:1, which
   * was the large-text floor and no more, so a button label got measurably
   * better out of this change.
   */
  ink: {
    50: '#F4F4F4',
    /**
     * The wash. Used where a tint is genuinely the only tool — a search match
     * has no border to thicken and no control to fill.
     *
     * It sits 0.61 lightness points from `gray[300]`, which is a border. A
     * wash and a one-pixel border are not confused in practice, but nothing
     * else may be built on this step: a selected row takes a border, a chip
     * inverts, and an option row takes a control. See `docs/design-system/foundations/colour.md`.
     */
    100: '#DEDEDE',
    200: '#C6C6C6',
    300: '#9C9C9C',
    400: '#737373',
    500: '#484848',
    /** The primary fill, and the strongest ink. */
    600: '#131313',
    /**
     * The press.
     *
     * 3.24 lightness points under step 600, inside the 2-to-6 window the
     * contrast test requires. A near-black primary still has room to darken
     * because step 600 is not black: pure `#000000` leaves none.
     */
    650: '#0C0C0C',
    700: '#070707',
    800: '#030303',
    900: '#010101',
  },
  /**
   * Neutrals. The Material grey ramp, chroma 0.
   *
   * Steps 500, 600 and 700 sit darker than Material's own, because disabled
   * text read 1.88 and muted text 4.23 on the screen before they were dropped.
   */
  gray: {
    0: '#FFFFFF',
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#949494',
    600: '#6F6F6F',
    700: '#5D5D5D',
    800: '#424242',
    900: '#212121',
  },
  /**
   * Success, and cash in.
   *
   * Step 100 is the chip fill. Step 600 is the text, and it clears 4.5 on a
   * card. Both were cut against a warm screen at lightness 96.89; the cold
   * screen sits at 97.02, so the separation the test asserts still holds.
   */
  green: {
    50: '#F3FAF6',
    100: '#D8F4DE',
    200: '#D1ECDC',
    300: '#A2D9B9',
    400: '#74C597',
    500: '#179F51',
    600: '#007C37',
    700: '#00692D',
    800: '#0E5F31',
    900: '#094020',
  },
  /** Error, and cash out. */
  red: {
    50: '#FCF5F5',
    100: '#FFE1DD',
    200: '#F4D8D8',
    300: '#E9B1B1',
    400: '#DF8989',
    500: '#C93B3B',
    600: '#B53535',
    700: '#A12F2F',
    800: '#792323',
    900: '#501818',
  },
  /**
   * Warning. The carried name for this role is `notice`.
   *
   * Amber, hue 43. It was re-hued away from the orange brand in Aug 2026 so a
   * warning would not read as a button. There is no brand hue now, and it
   * stays where it is: a warning wants to be warm.
   */
  amber: {
    50: '#FCFAF3',
    100: '#F8EACC',
    200: '#EEE3BF',
    300: '#E7D49D',
    400: '#DABF6C',
    /** White on this reads 4.04:1. */
    500: '#9E790A',
    600: '#876500',
    700: '#7D5F08',
    800: '#604906',
    900: '#3E3004',
  },
  /**
   * Information, and only information — an informational banner or badge.
   *
   * **This is not an accent.** Nothing interactive is coloured. If an accent
   * is ever wanted, it gets its own ramp rather than borrowing this one, so
   * that "informational" and "interactive" never collapse onto one hue.
   */
  navy: {
    50: '#F3F8FC',
    100: '#D6EFFF',
    200: '#D0E4F4',
    300: '#A1CAE8',
    400: '#71AFDD',
    500: '#137AC6',
    600: '#116EB2',
    700: '#0F629E',
    800: '#0B4977',
    900: '#08314F',
  },
  /**
   * The assistant's own blue, and nothing else's.
   *
   * **This is the one accent this system has, and it is an identity rather
   * than a tone.** `navy` says "informational" and the four feedback ramps
   * answer the four tone questions; this one answers *whose* — the assistant's
   * own drawings: its orb in every placement, the orb's two ribbons, the pearl
   * and the floating orb's working glow. Never its sheet, a button, a menu, a
   * band, a bubble or the dock's row, and never app chrome, a chart or a
   * state, or the app acquires the accent hue the contract spent its evidence
   * removing. `docs/design-system/foundations/colour.md` §*The assistant's
   * blue* names that list and `colour.enforcement.test.ts` holds it.
   *
   * **Measured, not chosen.** OpenAI publishes no blue, so the steps were
   * sampled off the shipping ChatGPT voice orb (iOS): a near-white cyan core
   * falling through light-blue bands to a vivid #007AFF, with its in-product
   * chip blue at about #3B82F6. Both agree on a hue near 211, which is what
   * 500 holds; the lighter steps are the orb's own gradient, so the drawing
   * is spelled here and not as artwork in a shader.
   *
   * 500 is the darkest step a white word may sit on (4.54:1); 600 and below
   * are the ones a word may be written IN on a light ground.
   */
  azure: {
    50: '#E8FAFB',
    100: '#B8E8F8',
    200: '#88D8F8',
    300: '#38A8F8',
    400: '#0878F8',
    /** White on this reads 4.54:1, the same footing `navy` 500 stands on. */
    500: '#0773E8',
    600: '#0568D0',
    700: '#0A56A8',
    800: '#0A3F7A',
    900: '#082A4F',
  },
  /**
   * Unused by the theme; no semantic role points at it. It is here so a chart
   * or a category chip can reach it.
   */
  purple: {
    50: '#F6F6FD',
    100: '#EEEDFA',
    200: '#DDDCF5',
    300: '#BAB8EB',
    400: '#9895E1',
    500: '#534ECD',
    600: '#4B46B9',
    700: '#423EA4',
    800: '#322F7B',
    900: '#211F52',
  },
} as const satisfies Record<string, Ramp>;

/**
 * Dark mode primitives. A light tint does not survive a dark background, so
 * each hue ships its own ramp.
 *
 * The chromatic ramps carry six steps. `gray` inverts: step 0 is the deepest
 * background and step 950 the strongest text. The step number always means
 * "more contrast", in both modes.
 */
export const darkPalette = {
  /**
   * The primary on dark, and it **inverts**.
   *
   * Step 400 is a near-white fill and step 500 the resting fill under it, so a
   * press moves *up* the ramp rather than down: a light fill on a dark screen
   * cannot darken to show a press without reading as a different control.
   *
   * `interactive.text.onPrimary` is already `gray[0]` in this scheme, a
   * near-black, so the pairing lands on its own: near-black ink on a near-white
   * fill reads 17.31:1. This is the inversion Uber's Base expresses as a
   * `primaryA`/`primaryB` pair, and this theme already had the expensive half.
   *
   * Steps 50 to 300 stay dark. They are the chip fill and its edges, which sit
   * *on* the dark screen rather than lifting off it.
   */
  ink: {
    /** The chip fill. */
    50: '#1F1F1F',
    100: '#2B2B2B',
    200: '#3A3A3A',
    300: '#6E6E6E',
    /** The fill when pressed, and the lightest step. */
    400: '#EDEDED',
    /** The resting fill. */
    500: '#E6E6E6',
  },
  /**
   * The neutral ramp, chroma 0, at the same lightnesses the warm ramp held.
   *
   * The screen sits at 16.84% lightness and a card at 21.78%, a jump of 4.9
   * points. Step 150 exists because `surface.border.gray.subtle` used to point
   * at the same value as a card, which made every row divider invisible.
   */
  gray: {
    /** The well. A recessed field — an input, a table — is drawn with it. */
    0: '#060606',
    /** The screen. */
    50: '#0F0F0F',
    /** A card. */
    100: '#1A1A1A',
    /** A divider inside a card. */
    150: '#262626',
    /** A card edge. */
    200: '#313131',
    300: '#666666',
    400: '#999999',
    500: '#BDBDBD',
    600: '#DBDBDB',
    700: '#E6E6E6',
    800: '#EDEDED',
    900: '#F5F5F5',
    950: '#FAFAFA',
  },
  /** Step 50 is the chip fill, step 400 the text. */
  green: {
    50: '#1A2A1E',
    100: '#36423C',
    200: '#414F47',
    300: '#316748',
    400: '#1BBF62',
    500: '#179F51',
  },
  /** Step 400 is the text and clears 4.5 on a card. Step 50 is the chip fill. */
  red: {
    50: '#34201E',
    100: '#423636',
    200: '#4F4141',
    300: '#794C4C',
    400: '#F15653',
    500: '#C93B3B',
  },
  /** Notice on dark. Step 50 is the chip fill. */
  amber: {
    50: '#2E2413',
    100: '#423D33',
    200: '#4F473A',
    300: '#756344',
    400: '#F5B31A',
    500: '#9E790A',
  },
  /** Information on dark. Not an accent — see `palette.navy`. */
  navy: {
    50: '#192735',
    100: '#363D42',
    200: '#41494F',
    300: '#577183',
    400: '#1C91E8',
    500: '#137AC6',
  },
  /** The assistant's blue on dark. Its orb keeps the light ramp: see `palette.azure`. */
  azure: {
    50: '#172738',
    100: '#363D42',
    200: '#414C56',
    300: '#57708B',
    400: '#2A8DF8',
    500: '#0773E8',
  },
  /** Unused by the theme, same reason as the light ramp. */
  purple: {
    50: '#2E2E38',
    100: '#393845',
    200: '#41414F',
    300: '#5C5B81',
    400: '#7570FF',
    500: '#534ECD',
  },
} as const satisfies Record<string, Ramp>;

/**
 * Kivi's colours: the lapis disc, the light it is lifted toward and the deep it
 * sinks to, the paper its eyes and line are drawn in, the ink of its floor
 * shadow, and the three record pebbles of building. Named, not a ramp: each is
 * a part of one drawing, and none is a step of a scale. **The same values in
 * both schemes**: Kivi is a thing in the room, not a surface that inverts;
 * the dark scheme answers with a rim, not a new colour. Read only through
 * `agent.kivi.*` (`agent-colors.ts`).
 */
export const kiviPalette = {
  lapis: '#2B2FB2',
  light: '#4652D6',
  deep: '#1D1F86',
  paper: '#F2F1EC',
  shadow: '#131313',
  pebble1: '#9BA0EC',
  pebble2: '#B9BCF2',
  pebble3: '#D3D5F7',
} as const;

/**
 * Adds an alpha channel to a 6 digit hex colour.
 *
 * Several carried tokens are translucent overlays, so a border reads correctly
 * on any surface under it.
 */
export const alpha = (hex: string, opacity: number): string => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
